import React, { useMemo, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';

import { useOrderDetailContext } from 'providers/OrderDetailProvider';
import { useUserContext } from 'providers/UserProvider';

import Table from 'components/common/Table';
import OrderShipmentExpandedRow from 'components/add_order/order/OrderDetail/OrderShipments/OrderShipmentExpandedRow';
import ManualAwbModal from 'components/add_order/shipment/ManualAwbModal';
import VideoPlaybackModal from 'components/add_order/shipment/VideoPlaybackModal';

import { shipmentApi } from 'api';
import { getOrderDetailShipmentColumns } from 'constants/order';
import { PRINTABLE_INVOICE_STATUSES } from 'constants/shipment';
import {
  convertPdfToHtml,
  showAwbPdf,
  generateInvoicePdf,
  generateBlueDartAwbPdf,
} from 'utils/pdfGenerators';

const OrderShipments = ({ orderId }) => {
  const tableRef = useRef();
  const [manualAwbModal, setManualAwbModal] = useState({
    show: false,
    shipmentId: null,
  });
  const [loadingShipmentId, setLoadingShipmentId] = useState(null);
  const [videoModal, setVideoModal] = useState({
    show: false,
    shipmentId: null,
  });

  const { orderDetailData } = useOrderDetailContext();
  const { selectedTenant, selectedWarehouse } = useUserContext();
  const tenant = selectedTenant?.domains?.[0]?.domain;
  const warehouse = selectedWarehouse?.domain;

  const handleRefreshShipment = async shipmentId => {
    setLoadingShipmentId(shipmentId);
    try {
      await shipmentApi.refreshShipment(shipmentId);

      toast.success('Successfully generated AWB');
    } catch (error) {
      toast.error('Failed to generate AWB');
    } finally {
      setLoadingShipmentId(null);
      tableRef.current?.refetch();
    }
  };

  const handleDirectAwb = async shipmentId => {
    setLoadingShipmentId(shipmentId);
    try {
      const response = await shipmentApi.directAwb(shipmentId);

      if (response?.shipment_status === 'ready_to_ship') {
        toast.success('AWB generated successfully');
      } else {
        toast.warning(
          'AWB generation failed. Please try Manual AWB or Refresh Shipping.',
        );
      }
    } catch (error) {
      const message =
        error?.response?.data?.non_field_errors?.[0] ||
        'Failed to process Direct AWB';
      toast.error(message);
    } finally {
      setLoadingShipmentId(null);
      tableRef.current?.refetch();
    }
  };

  const handlePrintPdf = async shipmentId => {
    try {
      const response = await shipmentApi.getInvoiceDetail(shipmentId);

      const shipmentAWBFile = response?.shipment?.awb_file;
      const isBlueDart =
        response?.shipment?.shipping_provider?.shipping_provider_type ===
        'BLUEDART';

      if (isBlueDart) {
        generateBlueDartAwbPdf(response);
      } else if (shipmentAWBFile) {
        const awbHtml = await convertPdfToHtml(shipmentAWBFile);
        showAwbPdf(awbHtml, response);
      } else {
        generateInvoicePdf(response);
      }
    } catch (error) {
      toast.error('Error generating PDF');
    }
  };

  const extendedColumns = useMemo(() => {
    const refreshShipmentColumn = {
      accessorKey: 'refresh_shipment',
      header: '',
      enableSorting: false,
      cell: ({ row: { original } }) => {
        const { id, shipment_status, picklist } = original;

        if (
          shipment_status === 'created' &&
          picklist == null &&
          selectedWarehouse?.is_picklist_pass_enabled
        ) {
          return (
            <Button
              variant="outline-success"
              size="sm"
              disabled={loadingShipmentId === id}
              onClick={() => handleDirectAwb(id)}
            >
              {loadingShipmentId === id ? 'Processing...' : 'Direct AWB'}
            </Button>
          );
        }

        if (shipment_status === 'packed') {
          return (
            <div className="d-flex gap-2">
              <Button
                variant="outline-warning"
                size="sm"
                disabled={loadingShipmentId === id}
                onClick={() => handleRefreshShipment(id)}
              >
                {loadingShipmentId === id ? 'Processing...' : 'Regenerate AWB'}
              </Button>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() =>
                  setManualAwbModal({ show: true, shipmentId: id })
                }
              >
                Manual AWB
              </Button>
            </div>
          );
        }

        return null;
      },
    };

    const invoicePrintColumn = {
      accessorKey: 'none',
      header: '',
      enableSorting: false,
      cell: ({ row: { original } }) => {
        const { id, shipment_status } = original;

        if (PRINTABLE_INVOICE_STATUSES.includes(shipment_status)) {
          return (
            <FontAwesomeIcon
              className="cursor-pointer"
              icon="file-alt"
              onClick={() => handlePrintPdf(id)}
            />
          );
        }

        return null;
      },
    };

    const videoColumn = {
      accessorKey: 'videos',
      header: '',
      enableSorting: false,
      cell: ({ row: { original } }) => {
        if (!original.videos?.length) return null;

        return (
          <FontAwesomeIcon
            className="cursor-pointer text-info"
            icon="play-circle"
            onClick={() =>
              setVideoModal({ show: true, shipmentId: original.id })
            }
          />
        );
      },
    };

    return [
      ...getOrderDetailShipmentColumns(tenant, warehouse),
      refreshShipmentColumn,
      invoicePrintColumn,
      videoColumn,
    ];
  }, [
    tenant,
    warehouse,
    selectedWarehouse?.is_picklist_pass_enabled,
    loadingShipmentId,
  ]);

  return (
    <>
      <Table
        ref={tableRef}
        title={`Order #${orderDetailData?.channelOrderId} Shipments`}
        apiService={shipmentApi}
        columns={extendedColumns}
        fetchConfig={{
          method: 'getById',
          transformResponse: response => ({
            data: response?.results || [],
            totalItems: response?.results?.length || 0,
          }),
        }}
        resourceId={orderId}
        enablePagination={false}
        renderExpandedRow={row => (
          <OrderShipmentExpandedRow shipment={row.original} />
        )}
      />
      <ManualAwbModal
        show={manualAwbModal.show}
        onHide={() => setManualAwbModal({ show: false, shipmentId: null })}
        shipmentId={manualAwbModal.shipmentId}
        onSuccess={() => tableRef.current?.refetch()}
      />
      <VideoPlaybackModal
        show={videoModal.show}
        onHide={() => setVideoModal({ show: false, shipmentId: null })}
        shipmentId={videoModal.shipmentId}
      />
    </>
  );
};

export default OrderShipments;
